/**
 * Run-scoped native executor tools.
 *
 * The returned `toolDefs` are passed to `buildToolDefinitionsForProvider`
 * via the `extraTools` param so they appear in the model's tool list.
 * The returned `executors` map is used by the agent's dispatch wrapper
 * before falling back to the global `executeToolCallDetailed`.
 */

import {
  CHART_CAPABILITY_TOOLS,
  executeChartCapabilityTool,
  type ChartCapabilityToolDef,
} from "./chartCapabilityTools";
import type { ApplyModificationsResult } from "./excelService";
import {
  executeListSheetsTool,
  executeReadRangeTool,
} from "./executorReadTools";
import {
  GET_SNAPSHOT_TOOL,
  executeGetSnapshotTool,
} from "./executorSnapshotTools";
import {
  EXECUTOR_DIAGNOSTIC_TOOLS,
  executeFindCalcErrorsTool,
  executeFindCircularReferencesTool,
} from "./executorDiagnosticTools";
import {
  EXECUTOR_WRITE_TOOLS,
  executeActivateSheetTool,
  executeClearRangeTool,
  executeCopyWorksheetTool,
  executeCreateSheetTool,
  executeEditRangeStructureTool,
  executeFormatRangeTool,
  executeInsertCompanyLogoTool,
  executeLayoutRangeTool,
  executeManageNamedRangeTool,
  executeManageWorksheetTool,
  executeModifyWorkbookTool,
  executeRenameSheetTool,
  executeSetRangeFormulasTool,
  executeSetRangeValuesTool,
  executeSortRangeTool,
} from "./executorWriteTools";
import {
  EXECUTOR_REFRESH_TOOLS,
  executeRefreshFactSetDataTool,
  executeRecalculateWorkbookTool,
  type ExecutorRefreshFactSetData,
  type ExecutorRecalculateWorkbook,
} from "./executorRefreshTools";
import {
  EXECUTOR_ANNOTATION_TOOLS,
  executeDeleteCommentTool,
  executeDeleteNoteTool,
  executeGetAllCommentsTool,
  executeGetAllNotesTool,
  executeGetCommentTool,
  executeGetNoteTool,
  executeSetCommentTool,
  executeSetNoteTool,
} from "./executorAnnotationTools";
import {
  executeSetCitationsTool,
} from "./executorCitationTools";
import {
  ACTIVE_GRAPH_TOOLS,
  executeActiveGraphTool,
} from "./chartCreatorTools";
import {
  isExplicitForwardPeRequest,
  resolveForwardPeIntentSignals,
} from "./chartCapabilityRegistry";
import type { ExcelLangfuseRunContext } from "./langfuseTrace";
import type {
  ColumnSizingCellEvidenceMap,
  ColumnSizingHint,
  ColumnSizingKey,
} from "./columnSizingPolicy";
import type {
  ExcelDateSystem,
  ExcelDateSystemProbeOptions,
} from "./excelDateSystem";
import type {
  IdLookupOutputScope,
  WorksheetWorkloadOutputScope,
} from "./executorWorkload";
import type {
  ToolCallContext,
  ToolCallDetailedResult,
} from "./toolRegistry";
import {
  EXECUTOR_RANGE_CHART_TOOLS,
  executeRangeChartTool,
} from "./executorNativeChartTools";
import {
  EXECUTOR_NATIVE_CHART_TOOLS,
  executeNativeChartTool,
} from "./executorNativeWorkbookChartTools";
import {
  EXECUTOR_WORKBOOK_TOOLS,
  executeActivateWorkbookTool,
  executeCreateWorkbookTool,
  executeListOpenWorkbooksTool,
  executeOpenWorkbookTool,
} from "./executorWorkbookTools";
import {
  beginChartStoppingEvidence,
  finishChartStoppingEvidence,
  type ChartStoppingEvidence,
} from "./executorChartStopping";
import type {
  ExecutorPlanState,
  ExcelStepStatus,
  WorkbookModification,
} from "./types";
import type { FactSetFormulaFormatRegistry } from "./factsetFormulaFormatRegistry";
import type {
  CitationNoteIndex,
  SourceNoteAudit,
} from "./citationNotes";
import type { FactSetSpreadsheetNoteCapability } from "../shared/factset/hostContextService";

// ---------------------------------------------------------------------------
// Runtime context
// ---------------------------------------------------------------------------

/**
 * Callback invoked by control tools when plan state mutates, so the UI
 * can re-render live step progress.
 */
export type PlanStateChangeCallback = (state: ExecutorPlanState) => void;

export type {
  IdLookupOutputScope,
  WorksheetWorkloadOutputScope,
} from "./executorWorkload";

/**
 * Callback supplied by the executor runner to apply a workbook mutation burst.
 * The React host owns sheet reactivation, cancellation, workbook pinning, and
 * operation logging; the tool layer only validates and forwards the normalized
 * `WorkbookModification` payload.
 *
 * @param args - Normalized apply request from `modify_workbook`.
 * @returns The host apply result, including edited cells and deferred refresh metadata.
 */
export type ExecutorApplyWorkbookModifications = (args: {
  /** Mutation payload to apply immediately. */
  modifications: WorkbookModification;
  /** Optional sheet hint for pre-apply reactivation. */
  targetSheet?: string;
  /** Human-readable reason for logs/status, e.g. "executor modify_workbook". */
  reason: string;
  /** Current pinned workbook name for host targeting (may change after re-pin). */
  pinnedWorkbookName?: string;
  /**
   * Set when `modifications` was already run through
   * `normalizeFactSetFormatsForModification` with the run-scoped
   * `factSetFormatRegistry` (see `executeModifyWorkbookTool`). Tells the host
   * apply path (`applyWorkbookModifications` in `excelService.ts`) to skip its
   * own boundary normalization pass, which would otherwise re-run with a fresh
   * empty registry — unable to see this run's accumulated provenance — and
   * redundantly re-scan an already-normalized payload.
   */
  skipFactSetFormatNormalization?: boolean;
}) => Promise<ApplyModificationsResult>;

/**
 * Mutable run-level accumulators updated by write tools. These mirror the
 * fields packed into `ExecutorRunResult` so the native runner can share the
 * existing `ExcelApp.tsx` finalization path.
 */
export interface ExecutorRunAccumulators {
  /** Internal execution log lines used by verifier/final summary logic. */
  stepResults: string[];
  /** Internal map from executor step id to index in `stepResults`. */
  stepResultIndexById: Map<string, number>;
  /** User-facing step summaries, index-aligned with the current plan steps. */
  stepDisplayResults: string[];
  /** Persistable step statuses, index-aligned with the current plan steps. */
  stepStatuses: ExcelStepStatus[];
  /** Last successfully applied workbook modification payload. */
  lastModifications: WorkbookModification | null;
  /** All edited cell/range addresses returned by successful apply calls. */
  editedCells: string[];
  /** Concrete formula-write ranges that need a final hard-error read-back. */
  formulaWriteTargets: Array<{ sheetName: string; address: string }>;
  /** FDS formula ranges written this run that must not be replaced by guessed static data. */
  fdsFormulaWriteTargets?: Array<{ sheetName: string; address: string }>;
  /** Confirmed FDS formula-write count keyed by normalized `sheet!cell`. */
  fdsFormulaWriteAttempts?: Record<string, number>;
  /** Exact FDS cells verified unavailable after correction + refresh/recalc. */
  unavailableFdsFormulaTargets?: Array<{ sheetName: string; address: string }>;
  /** FactSet date-axis anchors and whether they return serials or YYYYMMDD keys. */
  fdsDateAxisWriteTargets?: Array<{
    sheetName: string;
    address: string;
    kind: "serial" | "key";
  }>;
  /** Worksheets touched by successful writes and eligible for one final autofit pass. */
  autofitSheetNames: Set<string>;
  /** Concrete content-write ranges whose columns are eligible for final autofit. */
  autofitTargets: Array<{ sheetName: string; address: string }>;
  /** Per-column text evidence used to size narrative columns in the final pass. */
  columnSizingHints?: ColumnSizingHint[];
  /** Address-keyed text evidence so rewrites replace prior sizing observations. */
  columnSizingCellEvidence?: ColumnSizingCellEvidenceMap;
  /** Columns sized explicitly this run, which automatic sizing must not override. */
  explicitColumnWidthKeys?: ColumnSizingKey[];
  /** Sheets with FDS-bound formulas that need trailing FactSet refresh. */
  fdsRefreshSheets: Set<string>;
  /** Whether any apply result requested a trailing refresh-all. */
  deferredRefreshAllRequested: boolean;
  /** Deferred recalc mode requested by workbook operations, if any. */
  deferredRecalcMode: "recalculate" | "full" | "fullRebuild" | null;
  /** Whether any workbook edit was attempted during this run. */
  workbookEditAttempted: boolean;
  /** Exact research citation metadata available to writes in this run. */
  citationNoteIndex?: CitationNoteIndex;
  /** Verified Sheet!A1 to exact citation-ID provenance for this run. */
  citationCellProvenance?: Record<string, string[]>;
  /** Citation Note read-back audits produced by cited writes. */
  sourceNoteAudits?: SourceNoteAudit[];
  /** Existing chat-persistence map populated from cited `<u>` values. */
  retrievedCitedValuesByUuid?: Record<string, string>;
}

/**
 * Lightweight per-run session state that write tools need but should not
 * derive themselves from global UI state.
 */
export interface ExecutorRunSessionState {
  /** Sheet captured at run start for safe apply fallback. */
  sessionLockSheet: string;
  /** Exact user request for honoring explicit formatting overrides. */
  userRequest?: string;
  /** Optional workbook pin for host writes. Mutated by apply_template re-pin. */
  pinnedWorkbookName?: string;
  /**
   * Set by apply_template resolution when the remaining steps cannot safely
   * execute (warning-stop). The runner returns this so the shared finalize
   * tail can skip the post-plan verification pass.
   */
  skipFinalVerificationReason?: string;
  /** Allows an explicit user-requested formula-to-values conversion. */
  allowFdsFormulaOverwriteWithStaticValues?: boolean;
  /** True when this run restored an existing checkpoint. */
  isContinuation?: boolean;
  /** Host capability snapshot for generated citation Notes. */
  citationNoteCapability?: FactSetSpreadsheetNoteCapability;
  /** Run-scoped circuit breaker after a citation Note transport failure. */
  citationNoteAttachmentDisabled?: boolean;
  /** Output-specific chart verification keyed by stable run step id. */
  chartStoppingEvidence?: Map<string, ChartStoppingEvidence>;
  /**
   * Set once create_native_chart has inserted and refreshed the target
   * workbook. Suppresses automatic same-run read-back even when the host timed
   * out returning optional chart metadata.
   */
  nativeChartCreateCompletedWorkbookName?: string;
  /**
   * Set after create_native_chart completes its own GetCharts verification.
   * Suppresses a redundant read_native_chart host pass in the same run only.
   */
  nativeChartCreateVerifiedWorkbookName?: string;
}

export interface ExecutorWorkbookHandoff {
  sourceWorkbookName?: string;
  targetWorkbookName: string;
  activeSheetName?: string;
  reason:
    | "apply_template"
    | "native_chart"
    | "open_workbook"
    | "create_workbook"
    | "save_workbook_as"
    | "activate_workbook";
}

/**
 * Per-run context that control tool executors close over. Created once
 * at agent run start and shared across all tool calls within that run.
 */
export interface ExecutorToolRuntimeContext {
  /** The mutable plan state for this run — tools mutate this directly. */
  planState: ExecutorPlanState;
  /** Called after every mutation so the UI reflects live progress. */
  onPlanStateChange: PlanStateChangeCallback;
  /** Applies a normalized workbook modification burst through the host. */
  applyWorkbookModifications: ExecutorApplyWorkbookModifications;
  /** Run-level result accumulators shared with write tools. */
  accumulators: ExecutorRunAccumulators;
  /** Workbook/sheet session state for this executor run. */
  session: ExecutorRunSessionState;
  /**
   * Moves the in-flight conversation/plan to a newly activated workbook.
   * The callback must complete before target-workbook plan updates continue.
   */
  onWorkbookHandoff?: (
    handoff: ExecutorWorkbookHandoff,
  ) => void | Promise<void>;
  /**
   * Announces that a tool is about to create a workbook and move this
   * conversation into it. Excel brings a new workbook forward the moment it is
   * created — seconds before its sheets exist and the run can call
   * `onWorkbookHandoff` — and the task pane otherwise follows Excel and shows
   * that workbook's empty chat in between. While the returned release callback
   * is outstanding the pane stays on this conversation. Always release it.
   */
  beginWorkbookHandoff?: () => () => void;
  /** Trusted source/file scope captured when the user submitted this run. */
  researchContext?: ToolCallContext["researchContext"];
  /**
   * Exact resolved guidance for user-authored Skills, applied to every tabular
   * interpretation this run performs. Deliberately not part of the workload's
   * checkpointed query options: a restored workload re-derives it from the live
   * run context, so a resumed run can never replay a stale policy.
   */
  userSkillGuidance?: ToolCallContext["userSkillGuidance"];
  /** Run attribution propagated to tabular query HTTP requests. */
  langfuseContext?: ExcelLangfuseRunContext;
  /** Injectable Office.js date-system probe; the workload caches it per workbook pin. */
  probeExcelDateSystem?: (
    options?: ExcelDateSystemProbeOptions,
  ) => Promise<ExcelDateSystem | null>;
  /** Registered worksheet target used by write guards to reject adjacent writes. */
  worksheetWorkloadOutputScope?: WorksheetWorkloadOutputScope;
  /** @deprecated Backward-compatible ID-lookup reservation alias. */
  idLookupOutputScope?: IdLookupOutputScope;
  /** Run cancellation signal used to release run-scoped workload reservations. */
  signal?: AbortSignal;
  /** Current iteration budget, including a start call executing now. */
  getRemainingExecutorIterations?: () => number;
  /** Formula provenance and value semantics captured during this run. */
  factSetFormatRegistry?: FactSetFormulaFormatRegistry;
  /** Optional: refresh FactSet data for sheets mid-run. */
  refreshFactSetData?: ExecutorRefreshFactSetData;
  /** Optional: trigger workbook recalculation mid-run. */
  recalculateWorkbook?: ExecutorRecalculateWorkbook;
}

// ---------------------------------------------------------------------------
// Tool defs aggregate
// ---------------------------------------------------------------------------

/** All run-scoped tool definitions for the native executor. */
export const EXECUTOR_RUNTIME_TOOLS: readonly ChartCapabilityToolDef[] = [
  GET_SNAPSHOT_TOOL,
  ...EXECUTOR_ANNOTATION_TOOLS,
  ...CHART_CAPABILITY_TOOLS,
  ...ACTIVE_GRAPH_TOOLS,
  ...EXECUTOR_WRITE_TOOLS,
  ...EXECUTOR_RANGE_CHART_TOOLS,
  ...EXECUTOR_NATIVE_CHART_TOOLS,
  ...EXECUTOR_REFRESH_TOOLS,
  ...EXECUTOR_WORKBOOK_TOOLS,
  ...EXECUTOR_DIAGNOSTIC_TOOLS,
];

// ---------------------------------------------------------------------------
// Executor factory
// ---------------------------------------------------------------------------

/**
 * Executor function signature matching the global `ToolExecutor` type.
 * Detailed results retain trace-only child metadata without exposing it to the
 * model-facing output.
 */
export type RuntimeToolExecutor = (
  rawInput: unknown,
  signal?: AbortSignal,
  callContext?: RuntimeToolCallContext,
) => Promise<string | ToolCallDetailedResult>;

export interface RuntimeWorkloadProgress {
  kind: "requests" | "closed_rows";
  completed: number;
  total: number;
  usable?: number;
  failed?: number;
}

/** Display-only context for the currently executing runtime tool call. */
export interface RuntimeToolCallContext {
  callId: string;
  iteration: number;
  reportWorkloadProgress?: (progress: RuntimeWorkloadProgress) => void;
}

async function executeFocusedWrite(
  ctx: ExecutorToolRuntimeContext,
  rawInput: unknown,
  executor: (ctx: ExecutorToolRuntimeContext, rawInput: unknown) => Promise<string>,
): Promise<string> {
  const modelInput =
    rawInput !== null && typeof rawInput === "object" && !Array.isArray(rawInput)
      ? rawInput as Record<string, unknown>
      : {};
  const result = await executor(ctx, {
    ...modelInput,
    stepId: ctx.planState.currentStepId ?? null,
  });
  try {
    const parsed = JSON.parse(result) as Record<string, unknown>;
    delete parsed.stepId;
    return JSON.stringify(parsed);
  } catch {
    return result;
  }
}

async function executeActiveGraphWithStopping(
  ctx: ExecutorToolRuntimeContext,
  rawInput: unknown,
  signal?: AbortSignal,
): Promise<string> {
  const record =
    rawInput && typeof rawInput === "object" && !Array.isArray(rawInput)
      ? (rawInput as Record<string, unknown>)
      : {};
  const operation =
    typeof record.operation === "string" ? record.operation : "";
  if (
    operation !== "create_active_graph" &&
    operation !== "edit_active_graph"
  ) {
    return executeActiveGraphTool(
      injectActiveGraphTargetSheet(rawInput, ctx.session),
      signal,
      activeGraphSemanticUserRequest(ctx, rawInput),
    );
  }
  const stepId = beginChartStoppingEvidence(ctx, {
    stepId: record.stepId,
    tool: "active_graph",
    operation,
  });
  if (!stepId) {
    return JSON.stringify({
      ok: false,
      applied: false,
      operation,
      error:
        `${operation} requires a valid stepId (or an unambiguous active chart step) before it can mutate a workbook.`,
      completionEvidence: { satisfied: false },
    });
  }
  const result = await executeActiveGraphTool(
    injectActiveGraphTargetSheet(rawInput, ctx.session),
    signal,
    activeGraphSemanticUserRequest(ctx, rawInput),
  );
  let parsed: Record<string, unknown> = {};
  try {
    parsed = JSON.parse(result) as Record<string, unknown>;
  } catch {
    // The unsatisfied evidence below reports the unreadable acknowledgement.
  }
  const completion =
    parsed.completionEvidence &&
    typeof parsed.completionEvidence === "object" &&
    !Array.isArray(parsed.completionEvidence)
      ? (parsed.completionEvidence as Record<string, unknown>)
      : {};
  const satisfied =
    parsed.ok === true &&
    parsed.applied === true &&
    completion.satisfied === true;
  finishChartStoppingEvidence(ctx, {
    stepId,
    tool: "active_graph",
    operation,
    satisfied,
    reason: satisfied
      ? undefined
      : typeof parsed.error === "string"
        ? parsed.error
        : "ActiveGraph preview, host mutation, and OLE read-back were not all confirmed.",
    details: completion,
  });
  return result;
}

/**
 * Creates run-scoped tool executors that close over the given runtime
 * context. These executors mutate `ctx.planState` directly and invoke
 * `ctx.onPlanStateChange` after every mutation.
 *
 * The returned map is keyed by tool name and used by the agent's
 * dispatch wrapper: it tries these first, then falls back to the
 * global `executeToolCallDetailed` for stateless tools.
 *
 * @param ctx - The per-run runtime context holding mutable plan state.
 * @returns Object with:
 *   - `toolDefs`: tool definitions to include in the model's tool list.
 *   - `executors`: name→executor map for dispatch.
 */
export function createExecutorToolRuntime(ctx: ExecutorToolRuntimeContext) {
  return {
    toolDefs: EXECUTOR_RUNTIME_TOOLS,
    executors: {
      modify_workbook: (rawInput) => executeModifyWorkbookTool(ctx, rawInput),
      set_range_values: (rawInput) =>
        executeFocusedWrite(ctx, rawInput, executeSetRangeValuesTool),
      set_range_formulas: (rawInput) =>
        executeFocusedWrite(ctx, rawInput, executeSetRangeFormulasTool),
      format_range: (rawInput) =>
        executeFocusedWrite(ctx, rawInput, executeFormatRangeTool),
      clear_range: (rawInput) =>
        executeFocusedWrite(ctx, rawInput, executeClearRangeTool),
      edit_range_structure: (rawInput) =>
        executeFocusedWrite(ctx, rawInput, executeEditRangeStructureTool),
      layout_range: (rawInput) =>
        executeFocusedWrite(ctx, rawInput, executeLayoutRangeTool),
      sort_range: (rawInput) =>
        executeFocusedWrite(ctx, rawInput, executeSortRangeTool),
      manage_worksheet: (rawInput) =>
        executeFocusedWrite(ctx, rawInput, executeManageWorksheetTool),
      copy_worksheet: (rawInput) =>
        executeFocusedWrite(ctx, rawInput, executeCopyWorksheetTool),
      manage_named_range: (rawInput) =>
        executeFocusedWrite(ctx, rawInput, executeManageNamedRangeTool),
      insert_company_logo: (rawInput) =>
        executeFocusedWrite(ctx, rawInput, executeInsertCompanyLogoTool),
      create_sheet: (rawInput) => executeCreateSheetTool(ctx, rawInput),
      rename_sheet: (rawInput) => executeRenameSheetTool(ctx, rawInput),
      activate_sheet: (rawInput) => executeActivateSheetTool(ctx, rawInput),
      list_sheets: (rawInput) =>
        executeListSheetsTool(injectWorkbookName(rawInput, ctx.session)),
      read_range: (rawInput) =>
        executeReadRangeTool(injectWorkbookName(rawInput, ctx.session)),
      get_snapshot: (rawInput) =>
        executeGetSnapshotTool(injectWorkbookName(rawInput, ctx.session)),
      find_circular_references: (rawInput, signal) =>
        executeFindCircularReferencesTool(ctx, rawInput, signal),
      find_calc_errors: (rawInput, signal) =>
        executeFindCalcErrorsTool(ctx, rawInput, signal),
      get_comment: (rawInput, signal) =>
        executeGetCommentTool(rawInput, ctx.session.pinnedWorkbookName, signal),
      set_comment: (rawInput) => executeSetCommentTool(ctx, rawInput),
      delete_comment: (rawInput) => executeDeleteCommentTool(ctx, rawInput),
      get_all_comments: (rawInput, signal) =>
        executeGetAllCommentsTool(
          rawInput,
          ctx.session.pinnedWorkbookName,
          signal,
        ),
      get_note: (rawInput, signal) =>
        executeGetNoteTool(rawInput, ctx.session.pinnedWorkbookName, signal),
      set_note: (rawInput) => executeSetNoteTool(ctx, rawInput),
      set_citations: (rawInput) => executeSetCitationsTool(ctx, rawInput),
      delete_note: (rawInput) => executeDeleteNoteTool(ctx, rawInput),
      get_all_notes: (rawInput, signal) =>
        executeGetAllNotesTool(
          rawInput,
          ctx.session.pinnedWorkbookName,
          signal,
        ),
      list_chart_types: async (rawInput) =>
        executeChartCapabilityTool("list_chart_types", rawInput),
      active_graph: (rawInput, signal?: AbortSignal) =>
        executeActiveGraphWithStopping(ctx, rawInput, signal),
      range_chart: (rawInput) =>
        executeRangeChartTool(ctx, injectWorkbookName(rawInput, ctx.session)),
      native_chart: (rawInput, signal?: AbortSignal) =>
        executeNativeChartTool(ctx, rawInput, signal),
      // Dispatcher-only compatibility alias for persisted/paused runs. It is
      // intentionally absent from EXECUTOR_RUNTIME_TOOLS, so the model never
      // sees the old tool name.
      native_graph: (rawInput) =>
        executeRangeChartTool(ctx, injectWorkbookName(rawInput, ctx.session)),
      refresh_factset_data: (rawInput) =>
        executeRefreshFactSetDataTool(ctx, rawInput),
      recalculate_workbook: (rawInput) =>
        executeRecalculateWorkbookTool(ctx, rawInput),
      list_open_workbooks: (rawInput) =>
        executeListOpenWorkbooksTool(ctx, rawInput),
      open_workbook: (rawInput, signal) =>
        executeOpenWorkbookTool(ctx, rawInput, signal),
      create_workbook: (rawInput, signal) =>
        executeCreateWorkbookTool(ctx, rawInput, signal),
      activate_workbook: (rawInput, signal) =>
        executeActivateWorkbookTool(ctx, rawInput, signal),
    } satisfies Record<string, RuntimeToolExecutor>,
  };
}
// ---------------------------------------------------------------------------
// Run-scoped workbook name injection
// ---------------------------------------------------------------------------

/**
 * Injects `session.pinnedWorkbookName` into raw tool input when the model
 * omits `workbookName` or passes it as null/blank. Tool schemas often require
 * a nullable `workbookName`, and the model commonly sends null to mean "use
 * the current run target"; inside the executor, the run target is the pinned
 * workbook, not whichever workbook Excel happens to have active.
 *
 * @param rawInput - Parsed LLM arguments (may be null/non-object).
 * @param session - Current run session state.
 * @returns Input with `workbookName` injected if appropriate.
 */
export function injectWorkbookName(
  rawInput: unknown,
  session: ExecutorRunSessionState,
): unknown {
  if (!session.pinnedWorkbookName) return rawInput;
  if (rawInput === null || typeof rawInput !== "object") {
    return { workbookName: session.pinnedWorkbookName };
  }
  const r = rawInput as Record<string, unknown>;
  const workbookName = r.workbookName;
  if (typeof workbookName === "string" && workbookName.trim()) return r;
  return { ...r, workbookName: session.pinnedWorkbookName };
}

/**
 * Resolves the semantic chart request for deterministic metric-basis checks.
 * Referential follow-ups can recover intent from the full live run plan,
 * while an explicit trailing/exclusion instruction in the latest request wins.
 */
function activeGraphSemanticUserRequest(
  ctx: ExecutorToolRuntimeContext,
  rawInput: unknown,
): string | undefined {
  const input =
    rawInput && typeof rawInput === "object" && !Array.isArray(rawInput)
      ? (rawInput as Record<string, unknown>)
      : {};
  const requestedStepId =
    typeof input.stepId === "string" ? input.stepId.trim() : "";
  const currentStep =
    (requestedStepId
      ? ctx.planState.steps.find((step) => step.id === requestedStepId)
      : undefined) ??
    (ctx.planState.currentStepId
      ? ctx.planState.steps.find(
          (step) => step.id === ctx.planState.currentStepId,
        )
      : undefined) ??
    ctx.planState.steps.find(
      (step) => step.status === "running" || step.status === "verifying",
    );
  const signals = resolveForwardPeIntentSignals(ctx.session.userRequest, [
    currentStep?.planStep.description,
    currentStep?.planStep.action,
    ctx.planState.summary,
    ...ctx.planState.steps.flatMap((step) => [
      step.planStep.description,
      step.planStep.action,
    ]),
  ]);
  return (
    signals.find((signal) => isExplicitForwardPeRequest(signal)) ??
    ctx.session.userRequest
  );
}

/**
 * Injects the session-lock sheet into ActiveGraph creation calls when the model
 * omitted an explicit target sheet.
 */
function injectActiveGraphTargetSheet(
  rawInput: unknown,
  session: ExecutorRunSessionState,
): unknown {
  const withWorkbookName = injectWorkbookName(rawInput, session);
  if (withWorkbookName === null || typeof withWorkbookName !== "object") {
    return withWorkbookName;
  }

  const r = withWorkbookName as Record<string, unknown>;
  if (r.operation !== "create_active_graph" || !session.sessionLockSheet.trim()) {
    return withWorkbookName;
  }

  const target =
    r.target && typeof r.target === "object" && !Array.isArray(r.target)
      ? (r.target as Record<string, unknown>)
      : {};
  if ("sheetRef" in target) {
    return withWorkbookName;
  }

  return {
    ...r,
    target: {
      ...target,
      sheetRef: { name: session.sessionLockSheet },
    },
  };
}
